// Wave AW.3-React (2026-05-16) — 시세 제출 모달
// API: POST /buyer-app/rfq/:id/quotes { priceKrw, memo, currency, exchangeRate }

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import { useT, useLocale } from '../../i18n/I18nProvider';
import { formatKrwAmount, formatKrwAmountWithConvert, useExchangeRates } from '../../i18n/currency';

function formatKrw(n, t) { return formatKrwAmount(n, t); }

function AuctionBuyerQuoteModal(props) {
  var rfq = props.rfq || {};
  var existingQuote = props.existingQuote;       // { priceKrw, updateCount } 또는 null
  var onClose = props.onClose;
  var onSuccess = props.onSuccess;
  var navigate = useNavigate();
  var t = useT();
  var loc = useLocale();
  var rateState = useExchangeRates();

  var [manwon, setManwon] = useState(existingQuote && existingQuote.priceKrw ? String(Math.floor(existingQuote.priceKrw / 10000)) : '');
  var [memo, setMemo] = useState(existingQuote && existingQuote.memo ? existingQuote.memo : '');
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);

  var amount = manwon ? parseInt(manwon, 10) * 10000 : 0;
  var canSubmit = amount > 0 && !loading;

  var handleSubmit = function() {
    if (!canSubmit) return;
    setLoading(true); setError('');
    authBuyerFetch(API + '/buyer-app/rfq/' + rfq.id + '/quotes', {
      method: 'POST',
      body: JSON.stringify({ priceKrw: amount, memo: memo.trim(), currency: 'KRW', exchangeRate: 1 }),
    })
      .then(function(r){
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        return r.json().then(function(d){ return { status: r.status, body: d }; });
      })
      .then(function(res){
        setLoading(false);
        if (res.body && res.body.success) {
          if (onSuccess) onSuccess(res.body);
          if (onClose) onClose();
          return;
        }
        var code = (res.body && res.body.code) || '';
        if (code === 'PRICE_REQUIRED') setError(t('errPriceRequired'));
        else if (code === 'QUOTE_COMMITTED') setError(t('errQuoteCommitted'));
        else if (code === 'QUOTE_UPDATE_LIMIT') setError(t('errQuoteUpdateLimit'));
        else if (code === 'WITHDRAWAL_NO_REBID') setError(t('errWithdrawalNoRebid'));
        else if (code === 'RFQ_NOT_OPEN') setError(t('errRfqNotOpen'));
        else if (code === 'RFQ_EXPIRED') setError(t('errRfqExpired'));
        else if (code === 'BUYER_TRUST_TOO_LOW') setError(t('errBuyerTrustTooLow'));
        else setError(displayApiError(res.body, t, 'errQuoteSubmitFailed'));
      })
      .catch(function(e){
        if (e && e.message === 'unauthorized') return;
        setLoading(false); setError(t('commonNetwork'));
      });
  };

  var isUpdate = !!existingQuote;

  return (
    <div onClick={onClose}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}>
      <div onClick={function(e){e.stopPropagation();}}
        style={{ background: 'white', borderRadius: 16, width: 'calc(100% - 40px)', maxWidth: 480, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
            {isUpdate ? t('quoteModalTitleEdit') : t('quoteModalTitleNew')}
          </h2>
          <button type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: 24, color: '#94a3b8', cursor: 'pointer', padding: 0, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 14, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
            {rfq.year ? rfq.year + ' ' : ''}{rfq.model || t('promiseCardCarFallback')}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
            {rfq.carNo && rfq.carNo + ' · '}{rfq.mileage ? Number(rfq.mileage).toLocaleString() + ' km' : ''}
          </div>
        </div>

        {isUpdate && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 10, marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#1e40af', fontWeight: 600 }}>
              {t('quoteModalCurrent', { amount: formatKrw(existingQuote.priceKrw, t) })}
            </div>
            <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>
              {t('quoteModalUpdateWarning', { n: existingQuote.updateCount || 0 })}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            {t('quoteModalPriceLabel')}
          </label>
          <div style={{ position: 'relative' }}>
            <input type="number" value={manwon} onChange={function(e){setManwon(e.target.value);}}
              placeholder={t('quoteModalPricePlaceholder')}
              dir="ltr"
              inputMode="numeric"
              style={{
                width: '100%', height: 48, padding: '0 60px 0 16px',
                border: '1.5px solid #e2e8f0', borderRadius: 10,
                fontSize: 18, fontWeight: 700, outline: 'none',
                background: '#f8fafc',
                boxSizing: 'border-box',
              }}
            />
            <span style={{ position: 'absolute', right: 18, top: '50%', transform: 'translateY(-50%)', fontSize: 14, fontWeight: 600, color: '#475569' }}>{t('modalPriceSuffix')}</span>
          </div>
          {amount > 0 && (
            <div style={{ fontSize: 13, color: '#1B3A5C', fontWeight: 700, marginTop: 8 }}>
              = {formatKrwAmountWithConvert(amount, t, loc.locale, rateState.rates)}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
            {t('modalMemoLabel')}
          </label>
          <textarea value={memo} onChange={function(e){setMemo(e.target.value);}}
            maxLength={500} rows={2}
            placeholder={t('modalMemoPlaceholder')}
            style={{
              width: '100%', padding: '10px 14px',
              border: '1.5px solid #e2e8f0', borderRadius: 10,
              fontSize: 13, outline: 'none', resize: 'vertical',
              background: '#f8fafc',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', marginBottom: 16, color: '#991b1b', fontSize: 13 }}>
            ⚠ {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button type="button" onClick={onClose}
            style={{
              flex: 1, height: 48, background: 'white', color: '#475569',
              border: '1.5px solid #e2e8f0', borderRadius: 10,
              fontSize: 14, fontWeight: 700, cursor: 'pointer',            }}>
            {t('commonCancel')}
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit}
            style={{
              flex: 2, height: 48,
              background: canSubmit ? 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)' : '#cbd5e1',
              color: 'white', border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed',              boxShadow: canSubmit ? '0 4px 12px rgba(27,58,92,0.2)' : 'none',
            }}>
            {loading ? t('modalSubmittingQuote') : (isUpdate ? t('quoteModalTitleEdit') : t('quoteModalTitleNew'))}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AuctionBuyerQuoteModal;
